import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { dealershipService } from '../../services/dealershipService';
import DealershipHeader from './components/DealershipHeader';
import HeroSection from './components/HeroSection';
import RegistrationForm from './components/RegistrationForm';
import BenefitsSection from './components/BenefitsSection';
import ProcessSteps from './components/ProcessSteps';
import FAQSection from './components/FAQSection';
import THGNoticesSection from './components/THGNoticesSection';
import FooterSection from './components/FooterSection';
import SuccessModal from './components/SuccessModal';
import UploadConfirmationBanner from './components/UploadConfirmationBanner';

const DealershipLandingPage = () => {
  const { dealershipSlug } = useParams();
  const navigate = useNavigate();
  const [dealership, setDealership] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadedDocument, setUploadedDocument] = useState(null);

  useEffect(() => {
    const fetchDealership = async () => {
      // Reserved slugs that should not be treated as dealership identifiers
      const reservedSlugs = [
        'portal', 'admin', 'dashboard', 'login', 'signup', 'api',
        'admin-dashboard', 'application-processing', 'dealership-management',
        'voucher-verification', 'analytics-dashboard', 'customer-portal',
        'homepage', 'impressum', 'privacy-policy', 'terms-and-conditions-agb'
      ];

      // Check if the slug is reserved
      if (reservedSlugs?.includes(dealershipSlug?.toLowerCase())) {
        console.warn(`Reserved slug detected: ${dealershipSlug}. Redirecting to homepage.`);
        navigate('/', { replace: true });
        return;
      }

      if (!dealershipSlug) {
        setError('Ungültige Partner-URL. Bitte verwenden Sie einen gültigen Partner-Link.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const dealershipData = await dealershipService?.getBySlug(dealershipSlug);
        
        // 🔍 DIAGNOSTIC LOGGING for color data
        console.group('🎨 Dealership Color Diagnostics');
        console.log('Dealership Data:', dealershipData);
        console.log('Primary Color (camelCase):', dealershipData?.ciPrimaryColor);
        console.log('Secondary Color (camelCase):', dealershipData?.ciSecondaryColor);
        console.log('Has Colors:', !!(dealershipData?.ciPrimaryColor || dealershipData?.ciSecondaryColor));
        console.groupEnd();
        
        setDealership(dealershipData);
      } catch (err) {
        console.error('Error loading dealership:', err);
        setError('Partner konnte nicht geladen werden. Bitte überprüfen Sie die URL.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDealership();
  }, [dealershipSlug]);

  const handleFormSubmit = (formData) => {
    // Navigate to success page with application data AND dealership slug for smart redirect
    navigate('/application-success', { 
      state: { 
        applicationData: formData,
        dealershipSlug: dealershipSlug // Pass slug for redirect back to partner page
      },
      replace: true 
    });
  };

  const handleDocumentUpload = (documentData) => {
    setUploadedDocument(documentData);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Lade Partner-Daten...</p>
        </div>
      </div>
    );
  }

  if (error || !dealership) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground mb-4">
            Partner nicht gefunden
          </h1>
          <p className="text-muted-foreground mb-6">
            {error || 'Der angeforderte Partner konnte nicht gefunden werden.'}
          </p>
          <button
            onClick={() => navigate('/dealership-management')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth"
          >
            Zur Partner-Verwaltung
          </button>
        </div>
      </div>
    );
  }

  const voucherValue = dealership?.voucherValue || 150;
  const voucherCurrency = dealership?.voucherCurrency || 'Euro';
  const voucherDisplay = `${voucherValue} ${voucherCurrency}`;

  return (
    <>
      <Helmet>
        <title>{`Bis zu 340 ${voucherCurrency} Gutschein - ${dealership?.name} | Grid Balance`}</title>
        <meta
          name="description"
          content={`Sichern Sie sich bis zu 340 ${voucherCurrency} Gutschein für Ihr Elektrofahrzeug – einlösbar bei ${dealership?.name}. Jetzt für 2026 und 2027 registrieren.`}
        />
        <meta name="keywords" content="Gutschein, Elektroauto, E-Auto, THG-Quote, Grid Balance" />
        <meta property="og:title" content={`Bis zu 340 ${voucherCurrency} Gutschein - ${dealership?.name}`} />
        <meta property="og:description" content={`Bis zu 340 ${voucherCurrency} Gutschein bei ${dealership?.name} sichern – bei Registrierung für 2026 und 2027.`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <DealershipHeader dealership={dealership} />
        <HeroSection dealership={dealership} />
        
        <UploadConfirmationBanner uploadedDocument={uploadedDocument} />
        
        <RegistrationForm 
          onSubmit={handleFormSubmit} 
          dealershipId={dealership?.id}
          onDocumentUpload={handleDocumentUpload}
          dealership={dealership}
        />

        <section className="py-10 md:py-12 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-4 text-center">
              Wie funktioniert das?
            </h2>
            <div className="prose prose-sm max-w-none text-muted-foreground space-y-3">
              <p>
                Jeder Halter eines Elektrofahrzeugs hat Anspruch auf ein jährliches CO₂‑Zertifikat (THG-Quote). Dieses Zertifikat kann nur über zertifizierte Dienstleister gehandelt werden – nicht privat.
              </p>
              <p className="font-semibold text-foreground">
                Wir übernehmen das für Sie:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Sie treten uns Ihr Zertifikat für 2026 (und optional 2027) ab</li>
                <li>Wir kümmern uns um Beantragung & Abwicklung beim Umweltbundesamt</li>
                <li>Sie erhalten dafür einen Gutschein – einlösbar bei {dealership?.name || 'Ihrem Partner'}</li>
              </ul>
              <p className="text-center font-semibold text-foreground pt-2">
                Sie müssen nichts weiter tun. Wir erledigen alles im Hintergrund.
              </p>
            </div>
          </div>
        </section>

        <BenefitsSection dealership={dealership} />
        <ProcessSteps dealership={dealership} />
        <FAQSection dealership={dealership} />
        <THGNoticesSection />
        <FooterSection dealership={dealership} />
        
        <SuccessModal
          isOpen={showSuccessModal}
          onClose={handleCloseSuccessModal}
          customerData={submittedData}
        />
      </div>
    </>
  );
};

export default DealershipLandingPage;